import { ButtonLink } from "@/components/ui/Button";
import type { SatelliteEvent } from "@/interfaces/satellite_event";

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <p className="text-body text-ink-muted">
      <span className="text-ink font-bold">{label}:</span> {children}
    </p>
  );
}

// The Figma's "event info" card: the four facts somebody needs before they
// decide, on a torn scrap of paper, with the signup CTA underneath.
//
// The street address is deliberately not one of them unless the server sent it.
// It arrives only for people holding an RSVP and for the organizers, which is
// what the platform has always promised - so this shows the venue by name and
// says the address comes with the signup.
export default function EventInfoCard({
  event,
  signupHref,
  showSignup,
}: {
  event: SatelliteEvent;
  signupHref: string;
  showSignup: boolean;
}) {
  const addressLines = [
    event.venue_address,
    event.venue_address_line_2,
    [event.venue_city, event.venue_region].filter(Boolean).join(", "),
    event.venue_postal_code,
  ].filter((line) => line && line.trim().length > 0);

  return (
    <div className="border-line bg-surface-alt shadow-card flex flex-col gap-3 border-2 p-6 sm:p-8">
      <h2 className="font-display text-heading text-link self-center font-bold italic underline decoration-2 underline-offset-4">
        event info
      </h2>

      <div className="flex flex-col gap-2">
        {/* A postponed event has a reason its date is missing, and saying it
            beats "to be announced" - somebody holding an RSVP for the old date
            needs to know it moved, not that it was never set. */}
        <Row label="date">
          {event.date_label ??
            (event.awaiting_new_date
              ? event.postponed_from_label
                ? `moved from ${event.postponed_from_label}, new date coming`
                : "moved, new date coming"
              : "to be announced")}
        </Row>
        <Row label="time">
          {event.time_range_label ?? "to be announced"}
          {event.time_range_label && event.time_zone_label
            ? ` (${event.time_zone_label})`
            : ""}
        </Row>
        <Row label="venue">
          {addressLines.length > 0 ? (
            <span className="whitespace-pre-line">
              {[event.venue_name, ...addressLines].filter(Boolean).join("\n")}
            </span>
          ) : (
            <>
              {event.venue_name ?? event.location_label}
              <span className="text-ink-soft text-caption block">
                You'll get the full address when you sign up.
              </span>
            </>
          )}
        </Row>
        <Row label="boba">yes!</Row>
      </div>

      {showSignup && (
        <ButtonLink
          href={signupHref}
          variant="danger"
          className="mt-2 self-center rounded-full italic"
        >
          sign up &gt;&gt;
        </ButtonLink>
      )}
    </div>
  );
}
